// Onboarding organizace a brand kit (docs/PLAN.md 5.1, docs/OBSAH-MARKETING.md): služby, ceník,
// slogan, oblast, barva, logo a fotky týmu v org_settings.brand_kit; šablony služeb podle oboru.
import { z } from 'zod'
import { tradeSchema, type Trade } from './enums'

/** Částka v haléřích (stejný strop jako u zakázek). */
const money = z.number().int().min(0).max(1_000_000_000_00)

export const PRICE_UNITS = ['hour', 'piece', 'm2', 'm', 'visit', 'project'] as const
export const priceUnitSchema = z.enum(PRICE_UNITS)
export type PriceUnit = z.infer<typeof priceUnitSchema>

export const serviceItemSchema = z.object({
  name: z.string().trim().min(2).max(120),
  /** Orientační cena v haléřích, null = na dotaz. */
  priceMinor: money.nullable().default(null),
  unit: priceUnitSchema.default('hour'),
})
export type ServiceItem = z.infer<typeof serviceItemSchema>

/** PUT /v1/org/brand-kit (jen majitel). Vše volitelné, uloží se celý objekt. */
export const brandKitSchema = z.object({
  services: z.array(serviceItemSchema).max(60).default([]),
  slogan: z.string().trim().max(160).optional().or(z.literal('')),
  /** Oblast působení slovy („Příbram a okolí 30 km“). */
  area: z.string().trim().max(160).optional().or(z.literal('')),
  /** Barva značky pro web a materiály (hex), v aplikaci se nepoužívá. */
  primaryColor: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Barva ve tvaru #00d4ff')
    .optional()
    .or(z.literal('')),
  /** Klíč loga v úložišti (příloha entity org). */
  logoKey: z.string().max(500).optional().or(z.literal('')),
  /** Fotky týmu a auta (klíče příloh entity org). */
  teamPhotoKeys: z.array(z.string().max(500)).max(12).default([]),
  /** Výrazy, které se nesmí objevit v textech (Obsah, zprávy). */
  forbiddenWords: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
})
export type BrandKitInput = z.infer<typeof brandKitSchema>

/** GET /v1/org/brand-kit */
export interface BrandKit {
  services: ServiceItem[]
  slogan: string | null
  area: string | null
  primaryColor: string | null
  logoKey: string | null
  /** Podepsaná adresa loga (platí hodinu), null bez loga. */
  logoUrl: string | null
  teamPhotoKeys: string[]
  forbiddenWords: string[]
  /** Uživatel průvodce dokončil nebo přeskočil. */
  completedAt: string | null
}

/** Šablony služeb podle oboru (české názvy, ceny orientační, uživatel upraví). */
export const TRADE_TEMPLATES: Record<Trade, Array<{ name: string; unit: PriceUnit }>> = {
  electrician: [
    { name: 'Revize elektroinstalace', unit: 'visit' },
    { name: 'Výměna jističe', unit: 'piece' },
    { name: 'Montáž zásuvek a vypínačů', unit: 'piece' },
    { name: 'Rozvody v bytě', unit: 'project' },
    { name: 'Montáž svítidel', unit: 'piece' },
    { name: 'Hodinová sazba', unit: 'hour' },
  ],
  plumber: [
    { name: 'Výměna baterie', unit: 'piece' },
    { name: 'Oprava WC', unit: 'visit' },
    { name: 'Rozvody vody a odpadu', unit: 'project' },
    { name: 'Montáž bojleru', unit: 'piece' },
    { name: 'Havarijní výjezd', unit: 'visit' },
    { name: 'Hodinová sazba', unit: 'hour' },
  ],
  heating: [
    { name: 'Servis kotle', unit: 'visit' },
    { name: 'Výměna kotle', unit: 'project' },
    { name: 'Montáž tepelného čerpadla', unit: 'project' },
    { name: 'Odvzdušnění a vyvážení topení', unit: 'visit' },
    { name: 'Hodinová sazba', unit: 'hour' },
  ],
  mason: [
    { name: 'Zdění příček', unit: 'm2' },
    { name: 'Omítky', unit: 'm2' },
    { name: 'Obklady a dlažby', unit: 'm2' },
    { name: 'Rekonstrukce koupelny', unit: 'project' },
    { name: 'Hodinová sazba', unit: 'hour' },
  ],
  painter: [
    { name: 'Malování bytu', unit: 'm2' },
    { name: 'Nátěr fasády', unit: 'm2' },
    { name: 'Nátěr oken a dveří', unit: 'piece' },
    { name: 'Tapetování', unit: 'm2' },
    { name: 'Hodinová sazba', unit: 'hour' },
  ],
  carpenter: [
    { name: 'Kuchyň na míru', unit: 'project' },
    { name: 'Vestavěná skříň', unit: 'project' },
    { name: 'Montáž dveří', unit: 'piece' },
    { name: 'Oprava nábytku', unit: 'hour' },
    { name: 'Hodinová sazba', unit: 'hour' },
  ],
  service: [
    { name: 'Diagnostika', unit: 'visit' },
    { name: 'Servisní zásah', unit: 'hour' },
    { name: 'Pravidelná údržba', unit: 'visit' },
    { name: 'Výjezd', unit: 'visit' },
  ],
  other: [
    { name: 'Hodinová sazba', unit: 'hour' },
    { name: 'Výjezd', unit: 'visit' },
  ],
}

export function tradeTemplate(trade: string): ServiceItem[] {
  const parsed = tradeSchema.safeParse(trade)
  const list = TRADE_TEMPLATES[parsed.success ? parsed.data : 'other']
  return list.map((s) => ({ name: s.name, unit: s.unit, priceMinor: null }))
}

/** Cesta „aha do 5 minut“ (CO-PODCHYTIT část 5): kontakty, hlasovka, zakázka. */
export const ONBOARDING_STEPS = ['contacts', 'voice_note', 'job'] as const
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

/** GET /v1/onboarding: stav kroků spočítaný z dat firmy + příznak odložení. */
export interface OnboardingStatus {
  steps: Record<OnboardingStep, boolean>
  /** true = vše hotové nebo uživatel kartu zavřel. */
  dismissed: boolean
  /** Brand kit vyplněný (aspoň jedna služba nebo slogan). */
  brandKitDone: boolean
}
